import { useCallback, useMemo, useState, type DragEvent } from "react";

import {
  useRemoveVerdict,
  useUpsertVerdict,
  useVerdictsByBranch,
} from "@/application/use-cases/feature-roadmap-verdicts/use-verdicts.queries";
import { useCreateFeature } from "@/application/use-cases/features/use-features.create.queries";
import {
  useFeaturesList,
  useUpdateFeature,
} from "@/application/use-cases/features/use-features.queries";
import type { CreateFeatureFormValues } from "@/presentation/components/roadmaps/create-feature-dialog";
import type {
  FeatureRoadmapVerdictRow,
  FeatureRow,
} from "@/infrastructure/supabase/database.types";
import type { Domain } from "@/presentation/data/domains";
import { isVerdictForBranch, type RoadmapBranch, type Verdict } from "@/presentation/data/roadmaps";

export interface PendingDrop {
  feature: FeatureRow;
  verdict: Verdict;
}

const DRAG_MIME = "application/x-feature-id";

export function useBranchBoardPage(branch: RoadmapBranch, domainFilter: Domain | null) {
  const featuresQuery = useFeaturesList();
  const verdictsQuery = useVerdictsByBranch(branch);
  const upsertVerdict = useUpsertVerdict();
  const removeVerdict = useRemoveVerdict();
  const updateFeature = useUpdateFeature();
  const createFeature = useCreateFeature();

  const [draggedFeatureId, setDraggedFeatureId] = useState<string | null>(null);
  const [overVerdict, setOverVerdict] = useState<Verdict | null>(null);
  const [pendingDrop, setPendingDrop] = useState<PendingDrop | null>(null);
  const [infoFeature, setInfoFeature] = useState<FeatureRow | null>(null);
  const [editingFeature, setEditingFeature] = useState<FeatureRow | null>(null);
  const [createOpen, setCreateOpen] = useState(false);

  const allFeatures = featuresQuery.data ?? [];
  const allVerdicts = verdictsQuery.data ?? [];

  const verdictsByFeature = useMemo<Map<string, FeatureRoadmapVerdictRow>>(() => {
    const map = new Map<string, FeatureRoadmapVerdictRow>();
    for (const v of allVerdicts) {
      map.set(v.feature_id, v);
    }
    return map;
  }, [allVerdicts]);

  const featuresById = useMemo<Map<string, FeatureRow>>(() => {
    const map = new Map<string, FeatureRow>();
    for (const f of allFeatures) {
      map.set(f.id, f);
    }
    return map;
  }, [allFeatures]);

  const filteredFeatures = useMemo<FeatureRow[]>(() => {
    if (!domainFilter) return allFeatures;
    return allFeatures.filter((f) => f.domain === domainFilter);
  }, [allFeatures, domainFilter]);

  const stackFeatures = useMemo<FeatureRow[]>(
    () => filteredFeatures.filter((f) => !verdictsByFeature.has(f.id)),
    [filteredFeatures, verdictsByFeature]
  );

  const featuresByVerdict = useMemo<Map<Verdict, FeatureRow[]>>(() => {
    const map = new Map<Verdict, FeatureRow[]>();
    for (const f of filteredFeatures) {
      const v = verdictsByFeature.get(f.id);
      if (!v) continue;
      const bucket = map.get(v.verdict) ?? [];
      bucket.push(f);
      map.set(v.verdict, bucket);
    }
    return map;
  }, [filteredFeatures, verdictsByFeature]);

  const handleDragStart = useCallback((e: DragEvent<HTMLDivElement>, feature: FeatureRow) => {
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData(DRAG_MIME, feature.id);
    e.dataTransfer.setData("text/plain", feature.id);
    setDraggedFeatureId(feature.id);
  }, []);

  const handleDragOver = useCallback((e: DragEvent<HTMLDivElement>, verdict: Verdict) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setOverVerdict(verdict);
  }, []);

  const handleDragLeave = useCallback(() => {
    setOverVerdict(null);
  }, []);

  const handleDrop = useCallback(
    (e: DragEvent<HTMLDivElement>, verdict: Verdict) => {
      e.preventDefault();
      setOverVerdict(null);
      const id =
        e.dataTransfer.getData(DRAG_MIME) ||
        e.dataTransfer.getData("text/plain") ||
        draggedFeatureId;
      setDraggedFeatureId(null);
      if (!id) return;
      const feature = featuresById.get(id);
      if (!feature) return;
      if (!isVerdictForBranch(branch, verdict)) return;

      const existing = verdictsByFeature.get(id);
      if (existing) {
        // Moving between columns — change verdict directly, no dialog. Notes
        // live on the feature now, so they survive the column swap on their own.
        if (existing.verdict === verdict) return;
        upsertVerdict.mutate({
          featureId: id,
          branch,
          verdict,
        });
        return;
      }
      // First placement — open dialog so user can add a note.
      setPendingDrop({ feature, verdict });
    },
    [branch, draggedFeatureId, featuresById, upsertVerdict, verdictsByFeature]
  );

  const confirmDrop = useCallback(
    (notes: string | null) => {
      if (!pendingDrop) return;
      const trimmed = notes && notes.trim().length > 0 ? notes.trim() : null;
      // Notes live on the feature; classification lives on the verdict.
      // Only patch notes if the user actually typed something — empty input
      // shouldn't wipe an existing feature.notes value.
      if (trimmed !== null) {
        updateFeature.mutate({
          id: pendingDrop.feature.id,
          patch: { notes: trimmed },
        });
      }
      upsertVerdict.mutate({
        featureId: pendingDrop.feature.id,
        branch,
        verdict: pendingDrop.verdict,
      });
      setPendingDrop(null);
    },
    [branch, pendingDrop, updateFeature, upsertVerdict]
  );

  const cancelDrop = useCallback(() => setPendingDrop(null), []);

  const saveEdit = useCallback(
    (input: { verdict: Verdict; notes: string | null }) => {
      if (!editingFeature) return;
      const trimmed = input.notes && input.notes.trim().length > 0 ? input.notes.trim() : null;
      // Always patch notes from the edit dialog — the user sees the current
      // value and clearing it should persist.
      if (trimmed !== (editingFeature.notes ?? null)) {
        updateFeature.mutate({
          id: editingFeature.id,
          patch: { notes: trimmed },
        });
      }
      const currentVerdict = verdictsByFeature.get(editingFeature.id)?.verdict ?? null;
      if (input.verdict !== currentVerdict) {
        upsertVerdict.mutate({
          featureId: editingFeature.id,
          branch,
          verdict: input.verdict,
        });
      }
      setEditingFeature(null);
    },
    [branch, editingFeature, updateFeature, upsertVerdict, verdictsByFeature]
  );

  const removeFromBoard = useCallback(
    (feature: FeatureRow) => {
      removeVerdict.mutate({ featureId: feature.id, branch });
      setEditingFeature(null);
    },
    [branch, removeVerdict]
  );

  const openCreate = useCallback(() => setCreateOpen(true), []);
  const closeCreate = useCallback(() => setCreateOpen(false), []);

  const submitCreate = useCallback(
    (values: CreateFeatureFormValues) => {
      createFeature.mutate(
        {
          input: {
            title: values.title,
            subtitle: values.subtitle,
            description: values.description,
            domain: values.domain ?? undefined,
            component_id: values.component_id ?? undefined,
            journey: values.journey,
          },
          // Creating from the MVP board implies the feature is MVP — seed the
          // (all-features, mvp) verdict so it shows up on both boards.
          seedAsMvp: branch === "from-scratch",
        },
        {
          onSuccess: () => setCreateOpen(false),
        }
      );
    },
    [branch, createFeature]
  );

  return {
    isLoading: featuresQuery.isPending || verdictsQuery.isPending,
    error: featuresQuery.error ?? verdictsQuery.error ?? null,
    totalFeatures: allFeatures.length,
    stackFeatures,
    featuresByVerdict,
    verdictsByFeature,
    overVerdict,
    pendingDrop,
    infoFeature,
    editingFeature,
    handleDragStart,
    handleDragOver,
    handleDragLeave,
    handleDrop,
    confirmDrop,
    cancelDrop,
    openInfo: setInfoFeature,
    closeInfo: () => setInfoFeature(null),
    openEdit: setEditingFeature,
    closeEdit: () => setEditingFeature(null),
    saveEdit,
    removeFromBoard,
    createOpen,
    openCreate,
    closeCreate,
    submitCreate,
    isCreating: createFeature.isPending,
  };
}

export type BranchBoardPage = ReturnType<typeof useBranchBoardPage>;
